/**
 * Shared logout utility for all portals.
 *
 * Awaits server-side session invalidation (with timeout to avoid blocking UX),
 * includes CSRF token in the logout POST, and clears local session state.
 * Falls back to navigator.sendBeacon if the primary fetch fails or times out.
 *
 * @module shared/auth/logout
 */

/** Timeout in milliseconds for the logout request before falling back. */
const LOGOUT_TIMEOUT_MS = 4000;

/** Reads the CSRF token from the `ipa-csrf` cookie. */
export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)ipa-csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Same-origin path used for the logout POST. The web AFD has a dedicated
 * route (`/api/v1/auth/*` → APIM) so this stays on the frontend's own
 * origin — the browser does not send a CORS preflight, which avoids the
 * Azure Front Door / WAF behavior that strips preflight response headers
 * containing `csrf` (Story #2554, #2501).
 */
const LOGOUT_PATH = '/api/v1/auth/logout';

/** Options for {@link performLogout}. */
export interface PerformLogoutOptions {
  /** Override the default redirect path (defaults to '/login/'). */
  redirectPath?: string;
}

/**
 * Performs a full logout: invalidates the server-side session, clears local
 * state, and redirects to the login page.
 *
 * 1. Reads the CSRF token from the `ipa-csrf` cookie.
 * 2. POSTs to `/api/v1/auth/logout` (same-origin, see {@link LOGOUT_PATH}) with
 *    the CSRF token and a short timeout.
 * 3. If the fetch fails or times out, attempts `navigator.sendBeacon` as a fallback.
 * 4. Clears all session storage entries.
 * 5. Navigates to the login page.
 *
 * The function always clears local state and redirects, even if the server
 * request fails — the user is never trapped on a broken page.
 */
export async function performLogout(options?: PerformLogoutOptions): Promise<void> {
  const redirectPath = options?.redirectPath ?? '/login/';
  const logoutUrl = LOGOUT_PATH;
  const csrfToken = getCsrfToken();

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS);

    const headers: Record<string, string> = {
      'X-Correlation-ID': crypto.randomUUID(),
    };
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    await fetch(logoutUrl, {
      method: 'POST',
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
  } catch {
    // Primary fetch failed or timed out — try sendBeacon as a best-effort fallback.
    // sendBeacon survives page unloads and doesn't block, but cannot set custom headers.
    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        navigator.sendBeacon(logoutUrl);
      }
    } catch {
      // Beacon also failed — proceed with local cleanup regardless.
    }
  }

  // Always clear local state and redirect, even if the server request failed.
  clearSessionState();
  window.location.href = redirectPath;
}

/** Clears all IPA session-related entries from sessionStorage. */
function clearSessionState(): void {
  sessionStorage.removeItem('ipa_auth_token');
  sessionStorage.removeItem('ipa_auth_user');
  sessionStorage.removeItem('ipa_auth_authenticated');
  sessionStorage.removeItem('ipa_portal_grants');
}
